import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRingIcon, ClockIcon, MapPinIcon, XIcon } from 'lucide-react';
import type { Pet } from '../../data/animaux';
import { SearchZoneMap } from './SearchZoneMap';
interface LostFlowProps {
  pet: Pet;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}
type Step = 'lieu' | 'zone';
const places = ['Parc Centennial', '366, chemin Lakeshore', 'Position actuelle'];
const moments = ['Aujourd’hui · 15 h 20', 'Aujourd’hui · matin', 'Hier soir'];

/** Coordonnées approximatives de chaque dernier endroit connu, pour centrer la zone. */
const placeCoordinates: Record<string, [number, number]> = {
  'Parc Centennial': [45.4305, -73.87],
  '366, chemin Lakeshore': [45.4252, -73.8672],
  'Position actuelle': [45.4278, -73.865]
};
export function LostFlow({
  pet,
  open,
  onClose,
  onConfirm
}: LostFlowProps) {
  const [step, setStep] = useState<Step>('lieu');
  const [place, setPlace] = useState(places[0]);
  const [moment, setMoment] = useState(moments[0]);
  const [radius, setRadius] = useState(1.5);
  const choiceClass = (active: boolean) => `rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-slate-200 text-ville-navy hover:border-ville-indigo'}`;
  return <AnimatePresence data-id="element-156" data-mp="t8x7jsch9tud">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-157" data-mp="y66v12gb3vfk">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-158" data-mp="1c56kgcbn8ecs" />

          <motion.div role="dialog" aria-modal="true" aria-label={`${pet.name} est perdu`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-159" data-mp="1gxgikfmwwkq5">
            <div className="flex items-start justify-between gap-4" data-id="element-160" data-mp="17ukvzppprkj1">
              <div data-id="element-161" data-mp="1332u8zfv9fx0">
                <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-162" data-mp="132ko9b3ejnbw">{pet.name} est perdu</h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-163" data-mp="y4riuk9xaxco">
                  Nous allons vous aider à informer les personnes à proximité.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-164" data-mp="p3kz82bz96f7">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-165" data-mp="35prgogtttb6j" />
              </button>
            </div>

            <div className="mt-6" data-id="element-166" data-mp="39rlcaap5zozu">
              {step === 'lieu' && <div data-id="element-167" data-mp="39oqoc96xzm1o">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-168" data-mp="2d1cc6o8bmb9g">
                    <MapPinIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-169" data-mp="2hzdv995qyakx" />
                    Où {pet.name} a-t-il été vu pour la dernière fois ?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-170" data-mp="2qs7efhobdu42">
                    {places.map(option => <li key={option} data-id="element-171" data-mp="1uakt2uyj76hv">
                        <button type="button" onClick={() => setPlace(option)} className={choiceClass(place === option)} data-id="element-172" data-mp="1u9jggm1vaby1">
                          {option}
                        </button>
                      </li>)}
                  </ul>

                  <p className="mt-7 flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-173" data-mp="1a8qcrbfixb7e">
                    <ClockIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-174" data-mp="1oxmih1u3yc4u" />
                    Quand ?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-175" data-mp="1k4teig6gr7tt">
                    {moments.map(option => <li key={option} data-id="element-176" data-mp="1xvy74ybeyzun">
                        <button type="button" onClick={() => setMoment(option)} className={choiceClass(moment === option)} data-id="element-177" data-mp="1syy8h8gw8zdk">
                          {option}
                        </button>
                      </li>)}
                  </ul>
                </div>}

              {step === 'zone' && <div data-id="element-178" data-mp="diquuqdhh0c5">
                  <p className="text-[15px] font-bold text-ville-navy" data-id="element-179" data-mp="di7jjsic9a73">Zone de recherche</p>
                  <div className="relative mt-4 overflow-hidden rounded-2xl border border-slate-200" data-id="element-180" data-mp="8kpbe4c5az05">
                    <SearchZoneMap center={placeCoordinates[place]} radiusKm={radius} label={place} data-id="element-181" data-mp="2k6zsquof3wh7" />
                  </div>
                  <p className="mt-3 text-sm text-slate-600" data-id="element-182" data-mp="2agenwpi5hvpu">
                    <span className="font-bold text-ville-navy" data-id="element-183" data-mp="2afve5w2nmdmw">Dernier endroit connu :</span> {place} · {moment}
                  </p>
                  <label htmlFor="rayon" className="mt-4 block text-sm font-semibold text-slate-600" data-id="element-184" data-mp="21fq8prh0tpbd">
                    Rayon de diffusion : {radius.toFixed(1)} km
                  </label>
                  <input id="rayon" type="range" min={0.5} max={3} step={0.5} value={radius} onChange={event => setRadius(Number(event.target.value))} className="mt-2 w-full accent-ville-indigo" data-id="element-185" data-mp="25ez2uxfz3p7g" />
                  <p className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-id="element-186" data-mp="3qtvcx3prjx8k">
                    <BellRingIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-187" data-mp="3pb4tz8ht15fd" />
                    Un avis discret sera envoyé par notification web et mobile aux citoyens admissibles dans un rayon de{' '}
                    {radius.toFixed(1)} km. Vos coordonnées personnelles ne sont jamais partagées.
                  </p>
                </div>}
            </div>

            <div className="mt-8 flex items-center justify-between gap-4" data-id="element-188" data-mp="6zi2hepek8in">
              <button type="button" onClick={() => step === 'lieu' ? onClose() : setStep('lieu')} className="text-sm font-semibold text-slate-500 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-189" data-mp="71l30so6lk1m">
                {step === 'lieu' ? 'Annuler' : 'Modifier'}
              </button>
              <button type="button" onClick={() => {
            if (step === 'zone') {
              onConfirm();
              setStep('lieu');
              return;
            }
            setStep('zone');
          }} className={`rounded-full px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${step === 'zone' ? 'bg-red-600 focus-visible:ring-red-600' : 'bg-ville-navy focus-visible:ring-ville-indigo'}`} data-id="element-190" data-mp="px1f0sb2k99t">
                {step === 'zone' ? `Déclarer ${pet.name} perdu` : 'Continuer'}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}